// Performance Optimization Utilities
import React from 'react';

/** Run work after first paint — requestIdleCallback with setTimeout fallback. Returns cancel fn. */
export const deferNonCriticalWork = (callback: () => void, timeoutMs = 4000): (() => void) => {
  if (typeof window === 'undefined') {
    return () => {};
  }
  if (typeof window.requestIdleCallback === 'function') {
    const idleId = window.requestIdleCallback(callback, { timeout: timeoutMs });
    return () => window.cancelIdleCallback(idleId);
  }
  const timerId = window.setTimeout(callback, Math.min(timeoutMs, 3000));
  return () => window.clearTimeout(timerId);
};

// Image lazy loading utility
export const lazyLoadImage = (src: string, placeholder?: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(src);
    img.onerror = reject;
    img.src = src;
  });
};

// Component lazy loading with fallback
export const lazyLoadComponent = async (
  importFunc: () => Promise<any>,
  fallback?: React.ComponentType
) => {
  try {
    const module = await importFunc();
    return module.default || module;
  } catch (error) {
    console.error('Component lazy loading failed:', error);
    const DefaultFallback = () => React.createElement('div', null, 'Loading failed...');
    return fallback || DefaultFallback;
  }
};

/** Reserved for optional bundle introspection; no-op to avoid dev-console noise. */
export const trackBundleSize = () => {};

// Memory usage monitoring
export const trackMemoryUsage = () => {
  if ('memory' in performance) {
    const memory = (performance as any).memory;
  }
};

const GSI_URL = 'https://accounts.google.com/gsi/client';

/** Preload Google Sign-In script so login modal shows correct logo immediately (no default/placeholder flash). */
export const preloadGoogleGSIScript = (_caller?: string): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }
  if ((window as any).google?.accounts?.id) {
    return Promise.resolve();
  }
  const existing = document.querySelector(`script[src="${GSI_URL}"]`);
  if (existing) {
    return new Promise((resolve) => {
      (existing as HTMLScriptElement).onload = () => {
        resolve();
      };
      if ((window as any).google?.accounts?.id) resolve();
      else setTimeout(resolve, 3000);
    });
  }
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = GSI_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      resolve();
    };
    script.onerror = () => {
      resolve();
    };
    document.head.appendChild(script);
  });
};

// Critical resource preloader
export const preloadCriticalResources = () => {
  const criticalResources = [
    '/static/css/main.css',
    '/static/js/vendor.js',
    '/static/js/main.js'
  ];
  
  criticalResources.forEach(resource => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = resource.endsWith('.css') ? 'style' : 'script';
    link.href = resource;
    document.head.appendChild(link);
  });
};

// Performance metrics collection
export const collectPerformanceMetrics = () => {
  if (typeof window !== 'undefined' && 'performance' in window) {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    
    const metrics = {
      // Core Web Vitals
      FCP: 0, // First Contentful Paint
      LCP: 0, // Largest Contentful Paint
      FID: 0, // First Input Delay
      CLS: 0, // Cumulative Layout Shift
      
      // Loading metrics
      domContentLoaded: navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart,
      loadComplete: navigation.loadEventEnd - navigation.loadEventStart,
      
      // Network metrics
      dnsLookup: navigation.domainLookupEnd - navigation.domainLookupStart,
      tcpConnection: navigation.connectEnd - navigation.connectStart,
      tlsHandshake: navigation.secureConnectionStart > 0 ? navigation.connectEnd - navigation.secureConnectionStart : 0,
      
      // Resource timing
      resourcesLoaded: performance.getEntriesByType('resource').length,
    };

    return metrics;
  }
  
  return null;
};

// Image optimization utility
export const optimizeImageSrc = (
  src: string, 
  width?: number, 
  quality: number = 80,
  format: 'webp' | 'avif' | 'jpg' = 'webp'
): string => {
  // For development, return original src
  if (process.env.NODE_ENV === 'development') {
    return src;
  }
  
  // Add optimization parameters
  const url = new URL(src, window.location.origin);
  if (width) url.searchParams.set('w', width.toString());
  url.searchParams.set('q', quality.toString());
  url.searchParams.set('f', format);
  
  return url.toString();
};

// Bundle chunk preloader for critical routes
export const preloadRouteChunks = (routes: string[]) => {
  routes.forEach(route => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = `/static/js/${route}.chunk.js`;
    document.head.appendChild(link);
  });
};

// CSS optimization utility
export const loadCriticalCSS = (css: string) => {
  const style = document.createElement('style');
  style.textContent = css;
  style.id = 'critical-css';
  document.head.appendChild(style);
};

// Performance observer for monitoring
export const initPerformanceObserver = () => {
  if ('PerformanceObserver' in window) {
    // Largest Contentful Paint
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as any;
    });
    lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
    
    // First Input Delay
    const fidObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry: any) => {
      });
    });
    fidObserver.observe({ entryTypes: ['first-input'] });
    
    // Cumulative Layout Shift
    const clsObserver = new PerformanceObserver((list) => {
      let clsValue = 0;
      list.getEntries().forEach((entry: any) => {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
    });
    clsObserver.observe({ entryTypes: ['layout-shift'] });
  }
}; 